import { ClassProvider } from '@angular/core';
import { DefaultUrlSerializer, UrlSerializer, UrlTree } from '@angular/router';

/**
 * Encode parenthesis in url, which might be required to avoid routing errors
 * caused by invalid outlets defined in url.
 * @param url
 */
export function encodeParens(url: string): string {
  return url.replace(/\(/g, '%28').replace(/\)/g, '%29');
}

const LANG_REGEX = /(^\/[a-z]{2})(\/.+)?$/;

function getLangPrefix(url: string): string {
  return url.match(LANG_REGEX)?.[1] || '';
}

/**
 * CustomUrlSerializer alters default Angular url serialization behavior
 * by excluding a potentially existing language url prefix, so it won't
 * affect route resolution and logic relying on non-prefixed routes.
 * However, location still would show language-prefixed url.
 *
 */
export class CustomUrlSerializer extends DefaultUrlSerializer {
  private lang: string;

  /**
   * Parse the url by removing a potential lang prefix.
   * It catches errors due to wrong/missing url encoding by encoding the url before parsing.
   * It also will encode parenthesis for safety reasons.
   * @param url
   */
  parse(url: string): UrlTree {
    this.lang ??= getLangPrefix(url);
    const sanitizedUrl = this.removeLangPrefix(url);
    try {
      return super.parse(sanitizedUrl);
    } catch (e) {
      return super.parse(encodeParens(sanitizedUrl));
    }
  }

  serialize(tree: UrlTree): string {
    return this.addLangPrefix(super.serialize(tree));
  }

  private removeLangPrefix(url: string): string {
    return this.lang ? url.replace(LANG_REGEX, '$2') : url;
  }

  private addLangPrefix(url: string): string {
    return this.lang ? (this.lang + url).replace(/\/$/, '') : url;
  }
}

export const URL_SERIALIZER_PROVIDER: ClassProvider = {
  provide: UrlSerializer,
  useClass: CustomUrlSerializer
};
